import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';

import { AlertCircleIcon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import cn from 'classnames';
import { countries } from 'constants/countries';
import { FieldTypes } from 'constants/field-types-enum';
import { getIgnoredFieldsFromLogicJumps, meetsLogic } from 'constants/logic';
import { textWithReferences } from 'helpers/form';
import { useLocale } from 'hooks/locale';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { isPossiblePhoneNumber } from 'react-phone-number-input';
import { CustomButton } from 'views/CandidateApp/components/CustomButton/CustomButton';

import {
  Checkbox,
  DatePicker,
  Download,
  Email,
  FilePicker,
  FormMessage,
  MessageType,
  MultipleChoice,
  MultipleDocumentUpload,
  OpinionScale,
  PhoneNumberPicker,
  Select,
  Signature,
  StarRating,
  Textarea,
} from 'components/FormComponents';

import styles from './Form.module.scss';

// Stable empty array so MultiUpload doesn't get a new [] reference every render (avoids effect loop)
const EMPTY_UPLOADS = [];

const noHeaderComponents = [
  FieldTypes.Checkbox,
  FieldTypes.Consent,
  FieldTypes.Download,
  FieldTypes.ToggleSwitch,
];

// Component to handle Download field with upload ID - fetches fresh URL to avoid expired signed URLs
const DownloadField = ({ field, getCandidateUpload }) => {
  const [downloadUrl, setDownloadUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const uploadId = field?.options?.uploadId;

  useEffect(() => {
    if (uploadId && getCandidateUpload) {
      setLoading(true);
      // Wrap in Promise.resolve to handle cases where getCandidateUpload returns undefined or non-Promise
      Promise.resolve(getCandidateUpload(uploadId))
        .then(upload => {
          setDownloadUrl(upload?.url || null);
        })
        .catch(error => {
          console.error('Error fetching upload URL:', error);
          setDownloadUrl(null);
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setDownloadUrl(null);
      setLoading(false);
    }
  }, [uploadId, getCandidateUpload]);

  // If no getCandidateUpload function is available, we can't fetch the URL
  // In this case, we'll just show the component without a download URL
  if (!getCandidateUpload && uploadId) {
    console.warn('getCandidateUpload not available for Download field with uploadId:', uploadId);
  }

  return (
    <Download
      title={field?.options?.title}
      description={field?.options?.description}
      downloadUrl={loading ? null : downloadUrl}
    />
  );
};

export const Form = forwardRef(
  (
    {
      candidateId,
      country = 'NZ',
      form,
      brand,
      includeCandidateReferenceValidation = false,
      candidateEmail,
      emailCompareErrorMessage = '',
      candidateContact,
      contactCompareErrorMessage = '',
      buttonTitle = 'Save',
      textParams = {},
      locale = 'en_GB',
      rules = [],
      onSubmit = () => {},
      getCandidateUpload = () => {},
      deleteCandidateUpload = () => {},
      signCandidateUpload = () => {},
      createCandidateUpload = () => {},
    },
    ref,
  ) => {
    const { t } = useTranslation();
    const errorRef = useRef(null);

    const [showError, setShowError] = useState(false);
    const [blockMessage, setBlockMessage] = useState(null);
    const [logicIds, setLogicIds] = useState([]);
    const [ignoredFields, setIgnoredFields] = useState([]);

    const { dateHyphenFormat } = useLocale(locale);

    const {
      register,
      handleSubmit,
      watch,
      reset,
      formState: { errors },
      control,
    } = useForm({
      shouldFocusError: false,
      shouldUnregister: true,
      resetOptions: {
        keepErrors: true,
        keepValues: false,
        keepDirtyValues: false,
      },
    });

    // Expose functions
    useImperativeHandle(ref, () => ({
      reset,
    }));

    // Watch the controlling question (logic.field) whose answer is evaluated for each jump.
    // When logic.field and logic.from differ (e.g. "if Q1 is X, jump from Q4 to Q6"), we must
    // watch logic.field so logic re-runs when the controlling answer changes — not logic.from.
    useEffect(() => {
      if (!form) return;

      const logicOrders = (form.logic_jumps || [])
        .filter(l => l.operator !== 'always' && l.field != null)
        .map(l => l.field);
      const fieldsWithLogic = (form.fields || []).filter(field =>
        logicOrders.includes(field.order),
      );
      setLogicIds(fieldsWithLogic.map(f => `${f.id}`));
    }, [form]);

    // Hide fields by logic
    useEffect(() => {
      const maxOrder = form?.fields?.length > 0 ? Math.max(...form.fields.map(f => f.order)) : null;
      const ignoredFields = getIgnoredFieldsFromLogicJumps(
        form?.logic_jumps,
        form?.fields,
        watch,
        maxOrder,
      );

      setIgnoredFields(ignoredFields);
      for (const ignoreField of ignoredFields) {
        const field = form.fields.find(f => f.order === ignoreField);
        if (field) control.unregister(`${field.id}`);
      }
    }, [JSON.stringify(watch(logicIds)), form]);

    const onError = () => {
      errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setShowError(true);
    };

    const handleOnSubmit = data => {
      // Check block rules before submitting
      const blockRules = (rules || []).filter(r => r.rule_type === 'block' && r.active);
      for (const rule of blockRules) {
        const field = form?.fields?.find(f => Number(f.order) === Number(rule.field));
        if (!field) continue;
        const value = data[field.id.toString()];
        if (meetsLogic(rule, value)) {
          setBlockMessage(rule.message || 'You cannot proceed based on your answer.');
          return;
        }
      }
      setBlockMessage(null);

      const dataWithLabels = Object.keys(data).reduce((acc, fieldId) => {
        const field = form?.fields?.find(f => `${f.id}` === fieldId);
        acc[fieldId] = {
          value: data[fieldId],
          ...field,
        };
        return acc;
      }, {});
      onSubmit(data, dataWithLabels);
    };

    const renderField = field => {
      switch (field.type) {
        case FieldTypes.FreeText:
          return (
            <Textarea
              register={register}
              name={`${field.id}`}
              validators={{ required: field.required }}
              noErrorMessage
              className={styles.textInput}
              placeholder="Enter your answer"
              dynamicHeight={true}
            />
          );
        case FieldTypes.Email:
          return (
            <Email
              register={register}
              name={`${field.id}`}
              validators={{ required: field.required }}
              error={errors?.[`${field.id}`]?.message}
              className={styles.textInput}
              placeholder={t(field.placeholder, { defaultValue: 'Enter your email' })}
              dynamicHeight={true}
              compareEmailValidation={includeCandidateReferenceValidation}
              compareEmail={candidateEmail}
              compareErrorMessage={emailCompareErrorMessage}
            />
          );
        case FieldTypes.MultipleChoice:
          return (
            <Controller
              control={control}
              name={`${field.id}`}
              rules={{ required: field.required }}
              render={({ field: { onChange, value } }) => (
                <MultipleChoice
                  value={value}
                  onChange={onChange}
                  options={{
                    multipleSelection: field.options?.multiple_selection,
                    otherOption: field.options?.other_option,
                    options: field.options?.options,
                  }}
                />
              )}
            />
          );
        case FieldTypes.OpinionScale:
          return (
            <Controller
              control={control}
              name={`${field.id}`}
              rules={{ required: field.required }}
              render={({ field: { onChange, value } }) => (
                <OpinionScale options={field.options} value={value} onChange={onChange} />
              )}
            />
          );
        case FieldTypes.StarRating:
          return (
            <Controller
              control={control}
              name={`${field.id}`}
              rules={{ required: field.required }}
              render={({ field: { onChange, value } }) => (
                <StarRating options={field.options} value={value} onChange={onChange} />
              )}
            />
          );
        case FieldTypes.YesNo:
          return (
            <Controller
              control={control}
              name={`${field.id}`}
              rules={{ required: field.required }}
              render={({ field: { onChange, value } }) => (
                <MultipleChoice
                  value={value}
                  onChange={onChange}
                  options={{
                    multipleSelection: false,
                    otherOption: false,
                    options: [{ value: 'Yes' }, { value: 'No' }],
                  }}
                />
              )}
            />
          );
        case FieldTypes.Date:
          return (
            <Controller
              control={control}
              name={`${field.id}`}
              rules={{ required: field.required }}
              render={({ field: { onChange, value } }) => (
                <DatePicker
                  format={dateHyphenFormat}
                  outputFormat="YYYY-MM-DD"
                  value={value}
                  onChange={onChange}
                  useDefaultToday={!!field.options?.useDefault}
                />
              )}
            />
          );
        case FieldTypes.Country:
          return (
            <Select
              name={`${field.id}`}
              register={register}
              value={watch(`${field.id}`)}
              useDefault>
              {countries.map(country => (
                <Select.Item key={country.value} value={country.value}>
                  {country.name}
                </Select.Item>
              ))}
            </Select>
          );
        case FieldTypes.PhoneNumber:
          return (
            <Controller
              key={field.id}
              control={control}
              name={`${field.id}`}
              rules={{
                required: field.required,
                ...(includeCandidateReferenceValidation
                  ? {
                      validate: {
                        compare: value => {
                          // Handle both single contact and array of contacts
                          const contactsToCompare = Array.isArray(candidateContact)
                            ? candidateContact
                            : [candidateContact];
                          const isNotInArray = !contactsToCompare?.includes(value);
                          return isNotInArray || contactCompareErrorMessage;
                        },
                        validatePhoneNumber: value => {
                          // If value is empty/undefined/null, allow it (required validation is handled separately)
                          const trimmedValue = typeof value === 'string' ? value.trim() : value;
                          if (!trimmedValue) {
                            return true;
                          }
                          return (
                            isPossiblePhoneNumber(trimmedValue) || 'Enter a valid phone number'
                          );
                        },
                      },
                    }
                  : {}),
              }}
              render={({ field: { onChange, value, ref }, fieldState: { error } }) => (
                <PhoneNumberPicker
                  value={value}
                  defaultCountry={country}
                  className={styles.phone}
                  onChange={onChange}
                  error={error}
                />
              )}
            />
          );
        case FieldTypes.Download:
          return <DownloadField field={field} getCandidateUpload={getCandidateUpload} />;
        case FieldTypes.MultiUpload:
          return (
            <Controller
              key={field.id}
              control={control}
              name={`${field.id}`}
              rules={{ required: field.required }}
              render={({ field: { onChange, value } }) => (
                <MultipleDocumentUpload
                  question={{ text: field.text, description: field.description }}
                  value={value ?? EMPTY_UPLOADS}
                  onChange={onChange}
                  modelParams={{ form_field_id: field.id, candidate_id: candidateId }}
                  getUpload={getCandidateUpload}
                  signUpload={signCandidateUpload}
                  createUpload={createCandidateUpload}
                  deleteUpload={deleteCandidateUpload}
                  brand={brand}
                  accept=".pdf,.doc,.docx,image/*"
                  useModel
                />
              )}
            />
          );
        case FieldTypes.Upload:
          return (
            <Controller
              key={field.id}
              control={control}
              name={`${field.id}`}
              rules={{ required: field.required }}
              render={({ field: { onChange, value } }) => (
                <FilePicker
                  className={styles.filePicker}
                  onChange={onChange}
                  accept={{
                    'application/pdf': ['.pdf'],
                    'application/msword': ['.doc', '.docx'],
                    'image/*': [],
                  }}
                  value={value}
                  modelParams={{ form_field_id: field.id, candidate_id: candidateId }}
                  getUpload={getCandidateUpload}
                  signUpload={signCandidateUpload}
                  createUpload={createCandidateUpload}
                  deleteUpload={deleteCandidateUpload}
                />
              )}
            />
          );
        case FieldTypes.Signature:
          return (
            <Controller
              key={field.id}
              control={control}
              name={`${field.id}`}
              rules={{ required: field.required }}
              render={({ field: { onChange, value } }) => (
                <Signature
                  className={styles.signature}
                  modelParams={{ form_field_id: field.id, candidate_id: candidateId }}
                  signatureId={value}
                  onChange={onChange}
                  getUpload={getCandidateUpload}
                  signUpload={signCandidateUpload}
                  createUpload={createCandidateUpload}
                  deleteUpload={deleteCandidateUpload}
                />
              )}
            />
          );
        case FieldTypes.Checkbox:
          return (
            <Controller
              control={control}
              name={`${field.id}`}
              rules={{ required: field.required }}
              render={({ field: { onChange, value } }) => (
                <Checkbox
                  name={`${field.id}`}
                  checked={!!value}
                  onChange={onChange}
                  label={field.text}
                  textParams={textParams}
                />
              )}
            />
          );
      }
    };

    return (
      <>
        <form className="w-full">
          <div ref={errorRef} />
          <div className="flex flex-col gap-4 mb-16">
            {showError && (
              <FormMessage
                type={MessageType.Error}
                message="There are some invalid values. Please check the fields marked in red and re-submit."
                onClose={() => setShowError(false)}
              />
            )}
            {form?.message && (
              <FormMessage
                message={form?.message}
                type={MessageType.Info}
                closeable={false}
                noMargin
              />
            )}
          </div>
          {(form?.fields || [])
            ?.filter(field => !ignoredFields.includes(field.order))
            ?.map(field => (
              <div key={field.id} className={styles.container}>
                {!noHeaderComponents.includes(field.type) && (
                  <>
                    <p
                      className={cn(styles.question, {
                        [styles.questionWithError]: !!errors?.[`${field.id}`]?.type,
                      })}
                      dangerouslySetInnerHTML={{
                        __html:
                          textWithReferences(field.text, textParams) + (field.required ? '*' : ''),
                      }}
                    />
                    {field.use_description && (
                      <p
                        className={cn(styles.question, {
                          [styles.questionWithError]: !!errors?.[`${field.id}`]?.type,
                        })}
                        dangerouslySetInnerHTML={{
                          __html: textWithReferences(field.description, textParams),
                        }}
                      />
                    )}
                  </>
                )}
                {renderField(field)}
              </div>
            ))}

          <CustomButton
            className="u-margin-top--large"
            small
            brand={brand}
            onClick={handleSubmit(handleOnSubmit, onError)}>
            {buttonTitle}
          </CustomButton>
        </form>
        {blockMessage && (
          <div className="fixed inset-x-0 bottom-0 z-50 flex justify-center pb-6 px-4">
            <div className="bg-red-1 border border-red-2 rounded-2xl px-6 py-4 flex items-start gap-3 max-w-lg w-full shadow-lg">
              <HugeiconsIcon
                icon={AlertCircleIcon}
                size={20}
                className="text-red-2 shrink-0 mt-0.5"
              />
              <p className="text-neutral-7 text-xl">{blockMessage}</p>
            </div>
          </div>
        )}
      </>
    );
  },
);
